import { useEffect, useMemo, useState } from 'react';
import { BarList, Drawer } from '../../components/data';
import { Empty, ErrorBanner, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { downloadCsv, fileStamp, slugForFile } from '../../lib/download';
import { formatNumber, periodLabel } from '../../lib/format';
import { useHeldResource, useResource } from '../../lib/hooks';
import type { Product } from '../../lib/types';

interface Contract {
  id: number;
  name: string;
  number: string | null;
  type: string | null;
  start: string | null;
  end: string | null;
}
interface ContractService {
  id: number;
  contractId: number;
  contract: string;
  service: string;
  period: string | null;
  units: number | null;
  unitPrice: number | null;
  unitCost: number | null;
  total: number | null;
}
interface ServicesPayload {
  linked: boolean;
  companyName: string | null;
  unitsKnown?: boolean;
  contracts: Contract[];
  services: ContractService[];
}
interface LaborPayload {
  linked: boolean;
  companyName: string | null;
  months: { month: string; hours: number; revenue: number; cost: number; items: number; costMissing: number }[];
}

/** A line of the pricing workbench: a contract service as it is, or one added here. */
interface Line {
  key: string;
  name: string;
  /** Per month: 1 for Monthly, 1/12 for Yearly, and so on; 0 for one-time. */
  perMonth: number;
  period: string;
  units: number;
  cost: number;
  price: number;
  /** The line as it is on the contract today; absent for an added line. */
  current?: { units: number; price: number };
  added?: boolean;
  removed?: boolean;
}

/** A workbench proposal kept under a name (GET /am/clients/:id/renewals). */
interface SavedRenewal {
  id: string;
  name: string;
  notes: string | null;
  monthlyNow: number | null;
  monthlyProposed: number | null;
  lineCount: number;
  createdBy: string | null;
  createdUtc: string;
  updatedBy: string | null;
  updatedUtc: string;
  lines?: Line[];
}

const money = (v: number) => formatNumber(v, 'currency');
const day = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/** How much of a billing period falls in a month. One-time is not recurring. */
function perMonthOf(period: string | null): number {
  const p = (period ?? 'Monthly').toLowerCase();
  if (/semi/.test(p)) return 1 / 6;
  if (/quarter/.test(p)) return 1 / 3;
  if (/year|annual/.test(p)) return 1 / 12;
  if (/one|once/.test(p)) return 0;
  return 1;
}
const num = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Account Management › Contract renewals: what the client is on, what their
 * labor has billed, and a workbench to try a restructured contract.
 *
 * - Active contracts, from Autotask (the Services tab's read).
 * - Labor billed by month for the last twelve months (Autotask billing items
 *   of type Labor and Labor Adjustment).
 * - The pricing workbench: every service on the active contracts with our cost,
 *   the client's price, monthly and yearly totals and margin, where units and
 *   prices can be changed, lines dropped, and new lines added from the product
 *   catalogue or typed in — under a band showing the pricing as it is today,
 *   and the two compared.
 *
 * A proposal can be saved under a name, with notes, and reopened later from
 * Saved renewals (coo.AmContractRenewal). It is kept here only: nothing goes
 * to Autotask, which is only ever read. Export CSV keeps a copy as a file.
 */
export function AmContractRenewals({ clientId, clientName }: { clientId: string; clientName: string }) {
  const services = useHeldResource<ServicesPayload>(`/am/clients/${clientId}/autotask/services`);
  const labor = useHeldResource<LaborPayload>(`/am/clients/${clientId}/autotask/labor`);
  const catalogue = useResource<Product[]>('/products');
  const [lines, setLines] = useState<Line[] | null>(null);
  const [pick, setPick] = useState('');
  /* The month opened from the labor table, item by item. */
  const [laborMonth, setLaborMonth] = useState<string | null>(null);
  /* The contract opened to read its own pricing, line by line. */
  const [contract, setContract] = useState<Contract | null>(null);
  /* Contracts NOT priced in the workbench, by id. Empty means all of them,
     which is what a client with one contract should never have to think
     about; it only earns its keep on a client carrying a managed-services
     agreement beside a hardware lease nobody is renegotiating. */
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  /* Saved renewals, and the one open in the workbench (null: a new proposal). */
  const saved = useResource<SavedRenewal[]>(`/am/clients/${clientId}/renewals`);
  const [opened, setOpened] = useState<SavedRenewal | null>(null);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  /* The workbench as it was last saved or opened, to tell whether it has changed since. */
  const [savedAs, setSavedAs] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  /* The workbench starts from the contracts as they are, and again on Reset or a refresh.
     `skip` is passed rather than read from state so a toggle can build the
     next set of lines in the same tick it changes the ticks. */
  const fromContracts = (d: ServicesPayload, skip: Set<number> = excluded): Line[] =>
    d.services
      .filter((s) => !skip.has(s.contractId))
      .map((s) => {
        const units = s.units ?? 1;
        const price = s.unitPrice ?? 0;
        return {
          key: `s-${s.id}`,
          name: s.service,
          perMonth: perMonthOf(s.period),
          period: s.period ?? 'Monthly',
          units,
          cost: s.unitCost ?? 0,
          price,
          current: { units, price },
        };
      });

  /** Price this contract in the workbench, or stop pricing it. */
  function toggleContract(id: number) {
    /* Rebuilding the lines throws away whatever has been typed into them, so
       this asks first on the same terms Reset does. */
    if (!leaveOk() || !services.data) return;
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExcluded(next);
    setLines(fromContracts(services.data, next));
    /* The lines no longer match what was opened, so it is a new proposal. */
    setOpened(null);
    setSavedAs(null);
  }
  /* A saved renewal that is open keeps its own lines through a refresh. */
  useEffect(() => {
    if (services.data?.linked && !opened) setLines(fromContracts(services.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services.data]);

  const set = (key: string, patch: Partial<Line>) => setLines((ls) => (ls ?? []).map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const totals = useMemo(() => {
    const t = { curMonthly: 0, curCost: 0, newMonthly: 0, newCost: 0, oneTime: 0 };
    for (const l of lines ?? []) {
      if (l.current) {
        t.curMonthly += l.current.units * l.current.price * l.perMonth;
        t.curCost += l.current.units * l.cost * l.perMonth;
      }
      if (!l.removed) {
        t.newMonthly += l.units * l.price * l.perMonth;
        t.newCost += l.units * l.cost * l.perMonth;
        if (l.perMonth === 0) t.oneTime += l.units * l.price;
      }
    }
    return t;
  }, [lines]);
  const margin = (rev: number, cost: number) => (rev ? `${(((rev - cost) / rev) * 100).toFixed(1)}%` : '—');
  /* The proposed increase: the change in what the client pays, over what they pay now. */
  const increase = (now: number, proposed: number) => {
    if (!now) return proposed ? 'new' : '—';
    const p = ((proposed - now) / now) * 100;
    return `${p > 0 ? '+' : ''}${p.toFixed(1)}%`;
  };
  const increaseClass = (now: number, proposed: number) => (proposed > now ? 'is-up' : proposed < now ? 'is-down' : undefined);

  const snapshot = (ls: Line[] | null, n: string, t: string) => JSON.stringify({ ls, n: n.trim(), t: t.trim() });
  const dirty = savedAs !== null && savedAs !== snapshot(lines, name, notes);
  const leaveOk = () => !dirty || window.confirm('Leave this renewal? The changes since it was saved are lost.');

  /** Back to the contracts as they are, as a new proposal. */
  function startNew() {
    if (!leaveOk() || !services.data) return;
    setLines(fromContracts(services.data));
    setOpened(null);
    setName('');
    setNotes('');
    setSavedAs(null);
    setSaveError(null);
  }

  async function openSaved(id: string) {
    if (!leaveOk()) return;
    setBusy(true);
    setSaveError(null);
    try {
      const r = await api.get<SavedRenewal>(`/am/clients/${clientId}/renewals/${id}`);
      const ls = r.lines ?? [];
      setLines(ls);
      setOpened(r);
      setName(r.name);
      setNotes(r.notes ?? '');
      setSavedAs(snapshot(ls, r.name, r.notes ?? ''));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not open the renewal');
    } finally {
      setBusy(false);
    }
  }

  /** Save over the renewal that is open, or as a new one. */
  async function save(asNew: boolean) {
    if (!name.trim()) {
      setSaveError('Give the renewal a name.');
      return;
    }
    setBusy(true);
    setSaveError(null);
    const payload = {
      name: name.trim(),
      notes: notes.trim() || null,
      lines: lines ?? [],
      monthlyNow: Math.round(totals.curMonthly * 100) / 100,
      monthlyProposed: Math.round(totals.newMonthly * 100) / 100,
    };
    try {
      const r =
        opened && !asNew
          ? await api.put<SavedRenewal>(`/am/clients/${clientId}/renewals/${opened.id}`, payload)
          : await api.post<SavedRenewal>(`/am/clients/${clientId}/renewals`, payload);
      setOpened(r);
      setSavedAs(snapshot(lines, r.name, r.notes ?? ''));
      saved.reload();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the renewal');
    } finally {
      setBusy(false);
    }
  }

  async function deleteSaved(r: SavedRenewal) {
    if (!window.confirm(`Delete the saved renewal "${r.name}"?`)) return;
    setSaveError(null);
    try {
      await api.del(`/am/clients/${clientId}/renewals/${r.id}`);
      /* If it was open, the workbench keeps its lines, as a new proposal. */
      if (opened?.id === r.id) {
        setOpened(null);
        setSavedAs(null);
      }
      saved.reload();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not delete the renewal');
    }
  }

  const laborRows = labor.data?.months ?? [];
  const laborTotal = laborRows.reduce((t, m) => ({ hours: t.hours + m.hours, revenue: t.revenue + m.revenue, cost: t.cost + m.cost }), { hours: 0, revenue: 0, cost: 0 });

  function addLine() {
    if (!pick) return;
    if (pick === 'custom') {
      setLines((ls) => [...(ls ?? []), { key: `n-${Date.now()}`, name: 'New item', perMonth: 1, period: 'Monthly', units: 1, cost: 0, price: 0, added: true }]);
    } else {
      const p = catalogue.data?.find((x) => String(x.ProductId) === pick);
      if (p) {
        setLines((ls) => [
          ...(ls ?? []),
          { key: `p-${p.ProductId}-${Date.now()}`, name: p.Name, perMonth: perMonthOf(p.PeriodType), period: p.PeriodType, units: 1, cost: p.UnitCost ?? 0, price: p.UnitPrice, added: true },
        ]);
      }
    }
    setPick('');
  }

  const exportCsv = () =>
    downloadCsv(`${slugForFile(clientName)}_contract_proposal_${fileStamp()}.csv`, [
      ['Item', 'Period', 'Status', 'Units now', 'Price now', 'Units proposed', 'Price proposed', 'Our cost / unit', 'Monthly proposed', 'Yearly proposed'],
      ...(lines ?? []).map((l) => [
        l.name,
        l.period,
        l.removed ? 'Removed' : l.added ? 'Added' : l.current && (l.current.units !== l.units || l.current.price !== l.price) ? 'Changed' : 'As is',
        l.current?.units ?? '',
        l.current?.price ?? '',
        l.removed ? 0 : l.units,
        l.price,
        l.cost,
        l.removed ? 0 : Math.round(l.units * l.price * l.perMonth * 100) / 100,
        l.removed ? 0 : Math.round(l.units * l.price * l.perMonth * 1200) / 100,
      ]),
      [],
      ['Monthly now', Math.round(totals.curMonthly * 100) / 100, 'Monthly proposed', Math.round(totals.newMonthly * 100) / 100],
    ]);

  if (services.loading && !services.data) return <Loading label="Reading the client's contracts from Autotask" />;
  if (services.error) return <ErrorBanner message={services.error} />;
  if (!services.data) return null;
  if (!services.data.linked) {
    return (
      <Empty title="Not linked to Autotask">
        <p>This client has no Autotask company linked, so there are no contracts to read.</p>
      </Empty>
    );
  }
  const d = services.data;
  const monthlyOf = (contractId: number) =>
    d.services.filter((s) => s.contractId === contractId).reduce((t, s) => t + (s.units ?? 0) * (s.unitPrice ?? 0) * perMonthOf(s.period), 0);

  return (
    <div className="mt-4 cr">
      {/* Active contracts */}
      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>Active contracts</h3>
            <p className="sub">
              {d.contracts.length} active contract{d.contracts.length === 1 ? '' : 's'} for {d.companyName} in Autotask.
            </p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" disabled={services.refreshing || labor.refreshing} onClick={() => { services.refresh(); labor.refresh(); }}>
            {services.refreshing || labor.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
          </button>
        </div>
        {d.contracts.length === 0 ? (
          <div className="card-body"><p className="muted small">No active contracts.</p></div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 70 }} title="Price this contract in the workbench below">
                    Price it
                  </th>
                  <th>Contract</th>
                  <th>Type</th>
                  <th>Start</th>
                  <th>End</th>
                  <th className="num">Services</th>
                  <th className="num">Monthly value</th>
                </tr>
              </thead>
              <tbody>
                {d.contracts.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <label className="checkbox" title={`Price ${c.name || 'this contract'} in the workbench`}>
                        <input
                          type="checkbox"
                          checked={!excluded.has(c.id)}
                          onChange={() => toggleContract(c.id)}
                          aria-label={`Price ${c.name || 'this contract'} in the workbench`}
                        />
                      </label>
                    </td>
                    <td>
                      {/* The contract's own pricing, service by service —
                          the table says what it is worth a month, which is
                          not the same as saying what it is made of. */}
                      <button type="button" className="link-button" onClick={() => setContract(c)}>
                        {c.name || 'Contract'}
                      </button>
                      {c.number && <div className="xs muted">{c.number}</div>}
                    </td>
                    <td>{c.type ?? '—'}</td>
                    <td className="xs">{day(c.start)}</td>
                    <td className="xs">{c.end ? day(c.end) : 'No end date'}</td>
                    <td className="num">{d.services.filter((s) => s.contractId === c.id).length}</td>
                    <td className="num strong">{money(monthlyOf(c.id))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Labor billed by month */}
      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>Labor billed by month</h3>
            <p className="sub">
              The last twelve months of labor billed on invoices in Autotask (Labor and Labor Adjustment items that are on an invoice; posted
              but not yet invoiced is left out). Click a month for its items.
            </p>
          </div>
        </div>
        {labor.loading && !labor.data ? (
          <div className="card-body"><Loading label="Reading billed labor from Autotask" /></div>
        ) : labor.error ? (
          <div className="card-body"><ErrorBanner message={labor.error} /></div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="num">Hours billed</th>
                  <th className="num">Revenue</th>
                  <th className="num">Cost</th>
                  <th className="num">Margin</th>
                </tr>
              </thead>
              <tbody>
                {laborRows.map((m) => (
                  <tr key={m.month}>
                    <td>
                      {m.items > 0 ? (
                        <button type="button" className="link-button" onClick={() => setLaborMonth(m.month)}>
                          {periodLabel(m.month)}
                        </button>
                      ) : (
                        periodLabel(m.month)
                      )}
                    </td>
                    <td className="num">{formatNumber(m.hours)}</td>
                    <td className="num">{money(m.revenue)}</td>
                    <td className="num">
                      {money(m.cost)}
                      {m.costMissing > 0 && <span className="xs muted" title={`${m.costMissing} items with no cost in Autotask`}> *</span>}
                    </td>
                    <td className="num">{margin(m.revenue, m.cost)}</td>
                  </tr>
                ))}
                <tr className="cr-total">
                  <td>Last 12 months</td>
                  <td className="num">{formatNumber(laborTotal.hours)}</td>
                  <td className="num">{money(laborTotal.revenue)}</td>
                  <td className="num">{money(laborTotal.cost)}</td>
                  <td className="num">{margin(laborTotal.revenue, laborTotal.cost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      {laborMonth && <LaborMonthDrawer clientId={clientId} clientName={clientName} month={laborMonth} onClose={() => setLaborMonth(null)} />}

      {contract && (
        <ContractDrawer
          contract={contract}
          services={d.services.filter((x) => x.contractId === contract.id)}
          clientName={clientName}
          priced={!excluded.has(contract.id)}
          onClose={() => setContract(null)}
        />
      )}

      {/* Saved renewals */}
      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>Saved renewals</h3>
            <p className="sub">Proposals saved from the pricing workbench. Open one to carry on with it.</p>
          </div>
        </div>
        {saved.loading ? (
          <div className="card-body"><Loading label="Reading saved renewals" /></div>
        ) : saved.error ? (
          <div className="card-body"><ErrorBanner message={saved.error} /></div>
        ) : !saved.data?.length ? (
          <div className="card-body"><p className="muted small">None yet. Name a proposal in the workbench below and save it.</p></div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Renewal</th>
                  <th className="num">Lines</th>
                  <th className="num">Monthly then</th>
                  <th className="num">Monthly proposed</th>
                  <th className="num">Increase</th>
                  <th>Saved</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {saved.data.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <button type="button" className="link-button" disabled={busy} onClick={() => openSaved(r.id)}>
                        {r.name}
                      </button>
                      {opened?.id === r.id && <span className="xs muted"> · open below</span>}
                      {r.notes && <div className="xs muted cr-desc">{r.notes}</div>}
                    </td>
                    <td className="num">{r.lineCount}</td>
                    <td className="num">{r.monthlyNow === null ? '—' : money(r.monthlyNow)}</td>
                    <td className="num strong">{r.monthlyProposed === null ? '—' : money(r.monthlyProposed)}</td>
                    <td className="num">
                      {r.monthlyNow === null || r.monthlyProposed === null ? (
                        '—'
                      ) : (
                        <span className={increaseClass(r.monthlyNow, r.monthlyProposed)}>{increase(r.monthlyNow, r.monthlyProposed)}</span>
                      )}
                    </td>
                    <td className="xs">
                      {new Date(r.updatedUtc).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}
                      {r.updatedBy && <div className="muted">{r.updatedBy}</div>}
                    </td>
                    <td>
                      <button type="button" className="link-button xs" onClick={() => deleteSaved(r)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* The pricing workbench */}
      <section className="card">
        <div className="card-head">
          <div className="grow">
            <h3>Pricing workbench{opened ? ` — ${opened.name}` : ''}</h3>
            <p className="sub">
              Change units and prices, drop lines or add new ones to see a restructured contract. Save it under a name to come back to
              it; nothing goes to Autotask.
              {opened && ' Current pricing is as it was when this renewal was first worked on.'}
              {excluded.size > 0 &&
                ` Priced from ${d.contracts.length - excluded.size} of ${d.contracts.length} contracts — untick one above to leave it out.`}
            </p>
          </div>
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={startNew} title="Start again from the contracts as they are now">
              {opened ? 'New from contracts' : 'Reset'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={exportCsv} disabled={!lines?.length}>
              Export CSV
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="cr-save">
            <label className="cr-save-name">
              <span className="xs muted">Name</span>
              <input className="input input-sm" value={name} maxLength={200} placeholder="e.g. 2027 renewal, option A" onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="cr-save-notes">
              <span className="xs muted">Notes</span>
              <textarea className="textarea" rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <div className="row" style={{ gap: 'var(--space-2)' }}>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !lines?.length || (!!opened && !dirty)} onClick={() => save(false)}>
                {busy ? 'Saving…' : opened && !dirty ? 'Saved' : 'Save'}
              </button>
              {opened && (
                <button type="button" className="btn btn-secondary btn-sm" disabled={busy || !lines?.length} onClick={() => save(true)}>
                  Save as new
                </button>
              )}
            </div>
          </div>
          {saveError && <ErrorBanner message={saveError} />}
          <div className="cr-grid">
            <aside className="cr-side">
              <h4>Current pricing — what the client pays today</h4>
              <table className="table cr-current">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Units</th>
                    <th className="num">Our cost</th>
                    <th className="num">Price</th>
                    <th className="num">Monthly</th>
                    <th className="num">Yearly</th>
                  </tr>
                </thead>
                <tbody>
                  {(lines ?? [])
                    .filter((l) => l.current)
                    .map((l) => (
                      <tr key={l.key}>
                        <td title={l.name}>{l.name}</td>
                        <td className="num">{formatNumber(l.current!.units)}</td>
                        <td className="num">{money(l.cost)}</td>
                        <td className="num">{money(l.current!.price)}</td>
                        <td className="num">{money(l.current!.units * l.current!.price * l.perMonth)}</td>
                        <td className="num">{money(l.current!.units * l.current!.price * l.perMonth * 12)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>

              <div className="cr-compare">
                <div className="cr-increase">
                  <span>Proposed increase</span>
                  <strong className={increaseClass(totals.curMonthly, totals.newMonthly)}>{increase(totals.curMonthly, totals.newMonthly)}</strong>
                </div>
                <div>
                  <span>Monthly now</span>
                  <strong>{money(totals.curMonthly)}</strong>
                </div>
                <div>
                  <span>Monthly proposed</span>
                  <strong>{money(totals.newMonthly)}</strong>
                </div>
                <div>
                  <span>Change a month</span>
                  <strong className={totals.newMonthly - totals.curMonthly < 0 ? 'is-down' : 'is-up'}>
                    {totals.newMonthly - totals.curMonthly >= 0 ? '+' : ''}
                    {money(totals.newMonthly - totals.curMonthly)}
                  </strong>
                </div>
                <div>
                  <span>Yearly now</span>
                  <strong>{money(totals.curMonthly * 12)}</strong>
                </div>
                <div>
                  <span>Yearly proposed</span>
                  <strong>{money(totals.newMonthly * 12)}</strong>
                </div>
                <div>
                  <span>Change a year</span>
                  <strong className={totals.newMonthly - totals.curMonthly < 0 ? 'is-down' : 'is-up'}>
                    {totals.newMonthly - totals.curMonthly >= 0 ? '+' : ''}
                    {money((totals.newMonthly - totals.curMonthly) * 12)}
                  </strong>
                </div>
                <div>
                  <span>Our cost a month</span>
                  <strong>
                    {money(totals.curCost)} → {money(totals.newCost)}
                  </strong>
                </div>
                <div>
                  <span>Margin</span>
                  <strong>
                    {margin(totals.curMonthly, totals.curCost)} → {margin(totals.newMonthly, totals.newCost)}
                  </strong>
                </div>
                {totals.oneTime > 0 && (
                  <div>
                    <span>One-time items</span>
                    <strong>{money(totals.oneTime)}</strong>
                  </div>
                )}
              </div>
              {d.unitsKnown === false && <p className="xs muted">Autotask did not give the units; each line starts at 1.</p>}
            </aside>

            <div className="cr-main">
              <div className="table-wrap">
                <table className="table cr-bench">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="num">Units</th>
                      <th className="num">Our cost</th>
                      <th className="num">Client price</th>
                      <th className="num">Monthly total</th>
                      <th className="num">Yearly total</th>
                      <th className="num">Margin</th>
                      <th className="num">Increase</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(lines ?? []).map((l) => {
                      const monthly = l.units * l.price * l.perMonth;
                      const cost = l.units * l.cost * l.perMonth;
                      const changed = l.current && (l.current.units !== l.units || l.current.price !== l.price);
                      return (
                        <tr key={l.key} className={l.removed ? 'is-removed' : l.added ? 'is-added' : changed ? 'is-changed' : undefined}>
                          <td>
                            {l.added ? (
                              <input className="input input-sm" value={l.name} onChange={(e) => set(l.key, { name: e.target.value })} />
                            ) : (
                              l.name
                            )}
                            <div className="xs muted">
                              {l.period}
                              {l.added ? ' · added' : changed ? ' · changed' : ''}
                            </div>
                          </td>
                          <td className="num">
                            <input className="input input-sm cr-num" type="number" min={0} value={l.units} disabled={l.removed} onChange={(e) => set(l.key, { units: num(e.target.value) })} />
                          </td>
                          <td className="num">
                            {l.added ? (
                              <input className="input input-sm cr-num" type="number" step="0.01" value={l.cost} onChange={(e) => set(l.key, { cost: num(e.target.value) })} />
                            ) : (
                              money(l.cost)
                            )}
                          </td>
                          <td className="num">
                            <input className="input input-sm cr-num" type="number" step="0.01" value={l.price} disabled={l.removed} onChange={(e) => set(l.key, { price: num(e.target.value) })} />
                          </td>
                          <td className="num strong">{l.removed ? '—' : money(monthly)}</td>
                          <td className="num">{l.removed ? '—' : l.perMonth === 0 ? `${money(l.units * l.price)} once` : money(monthly * 12)}</td>
                          <td className="num">{l.removed ? '—' : margin(monthly, cost)}</td>
                          <td className="num">
                            {l.removed ? (
                              <span className="is-down">−100%</span>
                            ) : l.current ? (
                              changed ? (
                                <span className={increaseClass(l.current.units * l.current.price, l.units * l.price)}>
                                  {increase(l.current.units * l.current.price, l.units * l.price)}
                                </span>
                              ) : (
                                <span className="muted">—</span>
                              )
                            ) : (
                              <span className="is-up">new</span>
                            )}
                          </td>
                          <td>
                            {l.added ? (
                              <button type="button" className="link-button xs" onClick={() => setLines((ls) => (ls ?? []).filter((x) => x.key !== l.key))}>
                                Remove
                              </button>
                            ) : (
                              <button type="button" className="link-button xs" onClick={() => set(l.key, { removed: !l.removed })}>
                                {l.removed ? 'Keep' : 'Drop'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="cr-total">
                      <td colSpan={4}>Proposed</td>
                      <td className="num">{money(totals.newMonthly)}</td>
                      <td className="num">{money(totals.newMonthly * 12)}</td>
                      <td className="num">{margin(totals.newMonthly, totals.newCost)}</td>
                      <td className="num">
                        <span className={increaseClass(totals.curMonthly, totals.newMonthly)}>{increase(totals.curMonthly, totals.newMonthly)}</span>
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="row mt-2" style={{ gap: 'var(--space-2)' }}>
                <select className="select" value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Add a line">
                  <option value="">Add a line…</option>
                  <option value="custom">Custom item (type its name and prices)</option>
                  {(catalogue.data ?? []).map((p) => (
                    <option key={p.ProductId} value={p.ProductId}>
                      {p.Name} — {money(p.UnitPrice)} {p.PeriodType.toLowerCase()}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn btn-secondary btn-sm" disabled={!pick} onClick={addLine}>
                  Add
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

interface LaborItem {
  id: number;
  date: string | null;
  resource: string | null;
  ticketNumber: string | null;
  ticketTitle: string | null;
  task: string | null;
  workType: string | null;
  adjustment: boolean;
  description: string | null;
  hours: number;
  rate: number | null;
  revenue: number;
  cost: number | null;
  invoiced: boolean;
}

/**
 * One month of the client's billed labor, item by item: who, on what, the
 * work type, hours, rate, revenue and cost, with the month's hours by
 * technician. GET /am/clients/:id/autotask/labor/:month.
 */
/**
 * One contract, service by service — what it is actually made of.
 *
 * The table above says what a contract is worth a month; this says where
 * that number comes from, which is the question asked the moment anybody
 * disagrees with it. Everything here is already loaded with the contracts,
 * so opening one costs no read of Autotask.
 *
 * Margin is shown per line where a cost is known. A renewal conversation
 * that does not know which lines are carrying the contract is a conversation
 * about the total, and the total is rarely where the problem is.
 */
/* Unit prices to the cent. The page's `money` drops them, which is right for
   a monthly total and wrong for a rate: a $1.50 line shown as $2 is a third
   of the cost missing from a conversation about what to charge. */
const rate = (v: number | null) =>
  v === null ? '—' : v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

function ContractDrawer({
  contract,
  services,
  clientName,
  priced,
  onClose,
}: {
  contract: Contract;
  services: ContractService[];
  clientName: string;
  priced: boolean;
  onClose: () => void;
}) {
  const row = (x: ContractService) => {
    const per = perMonthOf(x.period);
    const units = x.units ?? 1;
    const monthly = units * (x.unitPrice ?? 0) * per;
    const costMonthly = units * (x.unitCost ?? 0) * per;
    return { ...x, units, per, monthly, costMonthly, margin: monthly - costMonthly };
  };
  const rows = services.map(row).sort((a, b) => b.monthly - a.monthly);
  const totals = rows.reduce(
    (t, r) => ({ monthly: t.monthly + r.monthly, cost: t.cost + r.costMonthly }),
    { monthly: 0, cost: 0 },
  );
  const anyCost = rows.some((r) => r.unitCost !== null && r.unitCost !== 0);

  const exportCsv = () =>
    downloadCsv(
      `${slugForFile(`${clientName} ${contract.name || 'contract'}`)}_${fileStamp()}.csv`,
      [
        ['Service', 'Period', 'Units', 'Unit price', 'Unit cost', 'Monthly value', 'Monthly cost', 'Monthly margin'],
        ...rows.map((r) => [
          r.service,
          r.period,
          r.units,
          r.unitPrice,
          r.unitCost,
          r.monthly,
          r.costMonthly,
          r.margin,
        ]),
      ],
    );

  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title={contract.name || 'Contract'}
      subtitle={`${clientName}${contract.number ? ` · ${contract.number}` : ''}`}
    >
      <div className="row-between wrap mb-3">
        <p className="small">
          <strong>{rows.length}</strong> service{rows.length === 1 ? '' : 's'} ·{' '}
          <strong>{money(totals.monthly)}</strong> a month
          {anyCost && ` · ${money(totals.monthly - totals.cost)} margin`}
          {!priced && ' · not priced in the workbench'}
        </p>
        <button type="button" className="btn btn-secondary btn-sm" disabled={rows.length === 0} onClick={exportCsv}>
          Export CSV
        </button>
      </div>
      <p className="xs muted mb-3">
        {day(contract.start)} – {contract.end ? day(contract.end) : 'no end date'}
        {contract.type ? ` · ${contract.type}` : ''}
      </p>
      {rows.length === 0 ? (
        <p className="muted small">No services on this contract.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Period</th>
                <th className="num">Units</th>
                <th className="num">Unit price</th>
                {anyCost && <th className="num">Unit cost</th>}
                <th className="num">Monthly</th>
                {anyCost && <th className="num">Margin</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.service}</td>
                  <td className="xs">{r.period ?? 'Monthly'}</td>
                  <td className="num">{formatNumber(r.units)}</td>
                  <td className="num">{rate(r.unitPrice)}</td>
                  {anyCost && <td className="num">{rate(r.unitCost)}</td>}
                  <td className="num strong">{money(r.monthly)}</td>
                  {anyCost && (
                    <td className="num">{r.unitCost === null ? '—' : money(r.margin)}</td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={anyCost ? 5 : 4} className="num strong">
                  A month
                </td>
                <td className="num strong">{money(totals.monthly)}</td>
                {anyCost && <td className="num strong">{money(totals.monthly - totals.cost)}</td>}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Drawer>
  );
}

function LaborMonthDrawer({ clientId, clientName, month, onClose }: { clientId: string; clientName: string; month: string; onClose: () => void }) {
  const data = useHeldResource<{ linked: boolean; month: string; items: LaborItem[] }>(`/am/clients/${clientId}/autotask/labor/${month}`);
  const items = data.data?.items ?? [];
  const totals = items.reduce((t, i) => ({ hours: t.hours + i.hours, revenue: t.revenue + i.revenue, cost: t.cost + (i.cost ?? 0) }), { hours: 0, revenue: 0, cost: 0 });
  const byTech = useMemo(() => {
    const m = new Map<string, { hours: number; revenue: number }>();
    for (const i of items) {
      const k = i.resource ?? 'Unknown';
      const g = m.get(k) ?? { hours: 0, revenue: 0 };
      g.hours += i.hours;
      g.revenue += i.revenue;
      m.set(k, g);
    }
    return [...m].sort((a, b) => b[1].hours - a[1].hours);
  }, [items]);
  const exportCsv = () =>
    downloadCsv(`${slugForFile(clientName)}_labor_${month}_${fileStamp()}.csv`, [
      ['Date', 'Technician', 'Ticket', 'Ticket title', 'Task', 'Work type', 'Hours', 'Rate', 'Revenue', 'Cost', 'Invoiced', 'Description'],
      ...items.map((i) => [i.date, i.resource, i.ticketNumber, i.ticketTitle, i.task, i.workType, i.hours, i.rate, i.revenue, i.cost, i.invoiced ? 'Yes' : 'No', i.description]),
    ]);
  return (
    <Drawer open wide onClose={onClose} title={`Labor billed — ${periodLabel(month)}`} subtitle={clientName}>
      {data.loading && !data.data && <Loading label="Reading the month's billing items from Autotask" />}
      {data.error && <ErrorBanner message={data.error} />}
      {data.data && (
        <>
          <div className="row-between wrap mb-3">
            <p className="small">
              <strong>{items.length}</strong> item{items.length === 1 ? '' : 's'} · <strong>{formatNumber(totals.hours)}</strong> hours ·{' '}
              <strong>{money(totals.revenue)}</strong> revenue · {money(totals.cost)} cost
            </p>
            <button type="button" className="btn btn-secondary btn-sm" disabled={items.length === 0} onClick={exportCsv}>
              Export CSV
            </button>
          </div>
          {byTech.length > 0 && (
            <div className="mb-4">
              <h4 className="mb-2">By technician</h4>
              <BarList rows={byTech.map(([name, g]) => ({ name, value: g.hours, display: `${formatNumber(g.hours)} h · ${money(g.revenue)}` }))} />
            </div>
          )}
          {items.length === 0 ? (
            <p className="muted small">No labor billed this month.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Technician</th>
                    <th>Ticket / task</th>
                    <th>Work type</th>
                    <th className="num">Hours</th>
                    <th className="num">Rate</th>
                    <th className="num">Revenue</th>
                    <th className="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id}>
                      <td className="xs" style={{ whiteSpace: 'nowrap' }}>{day(i.date)}</td>
                      <td>{i.resource ?? '—'}</td>
                      <td>
                        {i.ticketNumber ? (
                          <>
                            <span className="mono xs">{i.ticketNumber}</span> {i.ticketTitle}
                          </>
                        ) : (
                          i.task ?? '—'
                        )}
                        {i.description && <div className="xs muted cr-desc" title={i.description}>{i.description}</div>}
                      </td>
                      <td className="xs">
                        {i.workType ?? '—'}
                        {i.adjustment && <div className="xs muted">adjustment</div>}
                      </td>
                      <td className="num">{formatNumber(i.hours)}</td>
                      <td className="num">{i.rate === null ? '—' : money(i.rate)}</td>
                      <td className="num strong">{money(i.revenue)}</td>
                      <td className="num">{i.cost === null ? '—' : money(i.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
